import React, { useCallback, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Stack, Typography, IconButton, Alert, Chip,
  Tabs, Tab, CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import TuneIcon from "@mui/icons-material/Tune";
import VisibilityIcon from "@mui/icons-material/Visibility";

function toImgUrl(pathOrUrl, backendBase) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) return pathOrUrl;
  return `${backendBase}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

const DEFAULT_POS = { x: 50, y: 50, scale: 1 };

function LogoCircle({ logoSrc, pos, size = 130 }) {
  const { t } = useTranslation();
  const x = Number(pos?.x) ?? 50;
  const y = Number(pos?.y) ?? 50;
  const scale = Number(pos?.scale) || 1;

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        border: "3px solid #000",
        bgcolor: "#fff",
        overflow: "hidden",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {logoSrc ? (
        <Box
          component="img"
          src={logoSrc}
          alt="Λογότυπο"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: `${x}% ${y}%`,
            transform: `scale(${scale})`,
            transformOrigin: `${x}% ${y}%`,
            transition: "object-position 0.1s, transform 0.1s",
          }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: "#888", fontSize: 10, textAlign: "center", px: 1 }}>
          {t("modals.logo.noLogo")}
        </Typography>
      )}
    </Box>
  );
}

function PreviewPanel({ coverUrl, logoPreview, logoPosition }) {
  const { t } = useTranslation();
  const circleSize = 96;

  return (
    <Box
      sx={{
        width: "100%",
        position: "relative",
        pb: `${circleSize / 2 + 6}px`,
      }}
    >
      <Box
        sx={{
          width: "100%",
          borderRadius: 3,
          overflow: "hidden",
          position: "relative",
          "&::before": { content: '""', display: "block", paddingTop: "37.5%" },
          bgcolor: "rgba(0,0,0,0.25)",
          border: "1px solid",
          borderColor: "rgba(255,255,255,0.25)",
        }}
      >
        {coverUrl ? (
          <Box
            component="img"
            src={coverUrl}
            alt="Εξώφυλλο"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: "rgba(0,0,0,0.35)",
            }}
          >
            <Typography variant="body2" sx={{ color: "#fff" }}>
              {t("modals.cover.noCover")}
            </Typography>
          </Box>
        )}

        <Button
          variant="contained"
          size="small"
          startIcon={<PhotoCameraIcon />}
          disabled
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            pointerEvents: "none",
          }}
        >
          {t("modals.cover.uploadCover")}
        </Button>

        <Box
          sx={{
            position: "absolute",
            left: "50%",
            bottom: { xs: "-12%", sm: "-10%", md: "-5%" },
            transform: "translateX(-50%)",
            width: circleSize,
            height: circleSize,
            borderRadius: "50%",
            border: "2px solid",
            borderColor: "common.black",
            bgcolor: "common.white",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          {logoPreview ? (
            <Box
              component="img"
              src={logoPreview}
              alt="Λογότυπο"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: `${logoPosition?.x ?? 50}% ${logoPosition?.y ?? 50}%`,
                transform: `scale(${logoPosition?.scale ?? 1})`,
                transformOrigin: `${logoPosition?.x ?? 50}% ${logoPosition?.y ?? 50}%`,
              }}
            />
          ) : (
            <Typography variant="caption" sx={{ color: "#000" }}>
              {t("modals.logo.noLogo")}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          size="small"
          startIcon={<PhotoCameraIcon />}
          disabled
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            pointerEvents: "none",
          }}
        >
          {t("modals.logo.uploadLogo")}
        </Button>
      </Box>
    </Box>
  );
}

export default function LogoUploadModal({
  open,
  onClose,
  onSaved,
  currentCoverUrl = "",
  backendBase = "",
  token = "",
  notify,
}) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [tab, setTab] = useState(0);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [pos, setPos] = useState({ ...DEFAULT_POS });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    return () => {
      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (open) {
      setFile(null);
      setPreviewUrl("");
      setPos({ ...DEFAULT_POS });
      setErr("");
      setTab(0);
    }
  }, [open]);

  const handleFileChange = useCallback((e) => {
    const picked = e.target.files?.[0];
    e.target.value = "";

    if (!picked) return;

    if (!picked.type.startsWith("image/")) {
      const msg = t("modals.common.notImageFile");
      setErr(msg);
      notify?.("error", msg);
      return;
    }

    setErr("");

    if (previewUrl?.startsWith("blob:")) {
      URL.revokeObjectURL(previewUrl);
    }

    setFile(picked);
    setPreviewUrl(URL.createObjectURL(picked));
  }, [previewUrl, notify]);

  const handleSave = async () => {
    if (!file) {
      const msg = t("modals.common.noFileSelected");
      setErr(msg);
      notify?.("error", msg);
      return;
    }

    setErr("");
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("logo", file);
      formData.append("logoPosition", JSON.stringify(pos));

      const res = await axios.post(
        `${backendBase}/api/admin/images/client/logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = res.data || {};
      const newLogoPath = data?.data?.logo || data?.logo || "";
      const newPos = data?.data?.logoPosition || data?.logoPosition || pos;
      const message = data?.message || t("info.logoSuccess");

      onSaved?.(newLogoPath, newPos);
      notify?.("success", message);
      onClose();
    } catch (e) {
      console.error("Logo upload failed:", e);

      const message =
        e?.response?.data?.message ||
        e?.message ||
        t("info.logoFailed");

      setErr(message);
      notify?.("error", message);
    } finally {
      setSaving(false);
    }
  };

  const resolvedCover = currentCoverUrl ? toImgUrl(currentCoverUrl, backendBase) : "";

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#1a1a2e",
          color: "#fff",
          borderRadius: 3,
          border: "1px solid rgba(255,255,255,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <TuneIcon sx={{ color: "#fff" }} />
          <Typography fontWeight={900} variant="h6">
            {t("modals.logo.title")}
          </Typography>
        </Stack>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "rgba(255,255,255,0.6)" }}
          disabled={saving}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          px: 2,
          pt: 1,
          borderBottom: "1px solid rgba(255,255,255,0.08)",

          "& .MuiTab-root": {
            color: "rgba(255,255,255,0.6)",
            minHeight: 44,
            borderTopLeftRadius: 10,
            borderTopRightRadius: 10,
            textTransform: "none",
            fontWeight: 600,
            transition: "all 0.2s ease",
          },

          // 🔥 THIS is the important fix
          "& .MuiTab-root.Mui-selected": {
            color: "#111 !important",
            backgroundColor: "#fff",
          },

          // also force icon color
          "& .MuiTab-root.Mui-selected .MuiSvgIcon-root": {
            color: "#111 !important",
          },

          // optional hover polish
          "& .MuiTab-root:hover": {
            backgroundColor: "rgba(255,255,255,0.08)",
          },

          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
      >
        <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label={t("modals.logo.tabSettings")} />
        <Tab
          icon={<VisibilityIcon fontSize="small" />}
          iconPosition="start"
          label={t("common.preview")}
          disabled={!previewUrl}
        />
      </Tabs>


      <DialogContent sx={{ pt: 2.5 }}>
        {tab === 0 && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 1 }}>
                {t("modals.logo.fileInstruction")}
              </Typography>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff" }}
                >
                  {t("modals.common.selectFile")}
                </Button>

                {file && (
                  <Chip
                    label={file.name}
                    size="small"
                    onDelete={() => {
                      setFile(null);
                      if (previewUrl?.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
                      setPreviewUrl("");
                    }}
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      color: "#fff",
                      "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.6)" },
                    }}
                  />
                )}
              </Stack>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </Box>

            {previewUrl && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <LogoCircle logoSrc={previewUrl} pos={pos} size={110} />
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.45)" }}>
                  {t("modals.logo.circlePreviewHint")}
                </Typography>
              </Box>
            )}

            <Stack spacing={2.5}>
              <Box>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
                  {t("modals.logo.focusX")} <strong>{Number(pos.x) || 50}%</strong>
                </Typography>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Number(pos.x) || 50}
                  onChange={(e) => setPos((p) => ({ ...p, x: Number(e.target.value) }))}
                  style={{ width: "100%", accentColor: "#fff", cursor: "pointer" }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
                  {t("modals.logo.focusY")} <strong>{Number(pos.y) || 50}%</strong>
                </Typography>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={Number(pos.y) || 50}
                  onChange={(e) => setPos((p) => ({ ...p, y: Number(e.target.value) }))}
                  style={{ width: "100%", accentColor: "#fff", cursor: "pointer" }}
                />
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}>
                  {t("modals.logo.zoom")} <strong>×{(Number(pos.scale) || 1).toFixed(2)}</strong>
                </Typography>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={Number(pos.scale) || 1}
                  onChange={(e) => setPos((p) => ({ ...p, scale: Number(e.target.value) }))}
                  style={{ width: "100%", accentColor: "#fff", cursor: "pointer" }}
                />
              </Box>
            </Stack>

            {err && (
              <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                {err}
              </Alert>
            )}
          </Stack>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)", mb: 2, textAlign: "center" }}>
              {t("modals.logo.previewNote")}
            </Typography>
            <PreviewPanel
              coverUrl={resolvedCover}
              logoPreview={previewUrl}
              logoPosition={pos}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 2.5,
          pt: 1.5,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{ borderColor: "rgba(255,255,255,0.3)", color: "#fff", flex: 1 }}
        >
          {t("common.cancel")}
        </Button>

        {tab === 0 && previewUrl && (
          <Button
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => setTab(1)}
            disabled={saving}
            sx={{ borderColor: "#fff", color: "#fff", flex: 1 }}
          >
            {t("common.preview")}
          </Button>
        )}

        <Button variant="contained" onClick={handleSave} disabled={!file || saving} sx={{ flex: 1 }}>
          {saving ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} color="inherit" />
              <span>{t("common.saving")}</span>
            </Stack>
          ) : (
            t("common.save")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}